import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import socerPlayerIcon from "../../Assets/Icons/soccer-player.png";
import LoaderBox from "./LoaderBox";
import BottomNavigation from "../Navigation/BottomNavigation";
import Navigation from "../Navigation/Navigation";
import HomeFooter from "./HomeFooter";

const HomeScorers = ({ turnirId: propTurnirId }) => {
  const { turnirSlug } = useParams();
  const [turnirId, setTurnirId] = useState(propTurnirId || null);
  const [scorers, setScorers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hideDataMode, setHideDataMode] = useState(false);

  // Stanje za informacije o turniru (za Navigation)
  const [turnirInfo, setTurnirInfo] = useState({
    naziv: "",
    logo_url: "",
    lokacija: "",
    naziv_lokacije: "",
    features: {},
  });

  const fetchTopScorers = useCallback(async () => {
    try {
      setLoading(true);
      let currentTurnirId = turnirId;

      // Ako turnirId nije proslijeđen kroz props, a imamo turnirSlug u URL-u, dohvati ID turnira i info
      if (!currentTurnirId && turnirSlug) {
        const { data: turnirData, error: turnirError } = await supabase
          .from("admin_turniri")
          .select("id, naziv, logo_url, lokacija, naziv_lokacije, features")
          .eq("slug", turnirSlug)
          .single();

        if (turnirError) throw turnirError;

        if (turnirData) {
          currentTurnirId = turnirData.id;
          setTurnirId(turnirData.id);
          setTurnirInfo({
            naziv: turnirData.naziv || "",
            logo_url: turnirData.logo_url || "",
            lokacija: turnirData.lokacija || "",
            naziv_lokacije: turnirData.naziv_lokacije || "",
            features: turnirData.features || {},
          });
        }
      }

      if (!currentTurnirId) {
        setLoading(false);
        return;
      }

      // 1. Dohvati postavke (sakrivanje podataka) specifično za OVAJ turnir
      const { data: settings } = await supabase
        .from("admin_turniri")
        .select("hide_data_mode")
        .eq("id", currentTurnirId)
        .maybeSingle();

      if (settings) {
        const isLocalhost =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";
        setHideDataMode(isLocalhost ? false : settings.hide_data_mode || false);
      }

      // 2. Dohvati utakmice za OVAJ turnir iz tablice (turniri_utakmice)
      const { data: matches, error } = await supabase
        .from("turniri_utakmice")
        .select("events")
        .eq("tournament_id", currentTurnirId);

      if (error) throw error;

      const scorerMap = {};

      if (matches) {
        matches.forEach((match) => {
          if (match.events && Array.isArray(match.events)) {
            match.events.forEach((event) => {
              // Brojimo samo regularne golove i penale
              if (event.type === "goal" || event.type === "penalty") {
                const pName = event.player;
                const tName = event.teamName || "Nepoznat tim";

                if (!pName) return;

                // Kreiramo jedinstveni ključ (Ime + Tim) da izbjegnemo miješanje igrača istog imena
                const key = `${pName}-${tName}`;

                if (!scorerMap[key]) {
                  scorerMap[key] = {
                    name: pName,
                    team: tName,
                    goals: 0,
                  };
                }
                scorerMap[key].goals += 1;
              }
            });
          }
        });
      }

      // Pretvaranje u niz i sortiranje od najviše prema najmanje golova
      const finalScorers = Object.values(scorerMap)
        .filter((player) => player.goals > 0)
        .sort((a, b) => b.goals - a.goals)
        .slice(0, 10);

      setScorers(finalScorers);
    } catch (err) {
      console.error("Greška pri dohvaćanju strijelaca:", err.message);
    } finally {
      setLoading(false);
    }
  }, [turnirId, turnirSlug]);

  useEffect(() => {
    fetchTopScorers();
  }, [fetchTopScorers]);

  if (loading) {
    return (
      <>
        <Navigation
          turnirSlug={turnirSlug}
          turnirNaziv={turnirInfo.naziv}
          turnirLogo={turnirInfo.logo_url}
          lokacija={turnirInfo.lokacija}
          nazivLokacije={turnirInfo.naziv_lokacije}
          features={turnirInfo.features}
        />
        <div className="scorers-container">
          <LoaderBox text="Učitavanje strijelaca..." />
        </div>
        <BottomNavigation />
      </>
    );
  }

  if (hideDataMode) {
    return (
      <>
        <Navigation
          turnirSlug={turnirSlug}
          turnirNaziv={turnirInfo.naziv}
          turnirLogo={turnirInfo.logo_url}
          lokacija={turnirInfo.lokacija}
          nazivLokacije={turnirInfo.naziv_lokacije}
          features={turnirInfo.features}
        />
        <div className="scorers-container">
          <div className="scorers-header">
            <img src={socerPlayerIcon} alt="Strijelci icon" />
            <h3>Najbolji Strijelci</h3>
          </div>

          <div className="scorers-list">
            <div
              className="no-scorers-info"
              style={{ textAlign: "center", padding: "30px", color: "#888" }}
            >
              <p>Trenutno nema strijelaca za prikaz.</p>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </>
    );
  }

  return (
    <>
      <Navigation
        turnirSlug={turnirSlug}
        turnirNaziv={turnirInfo.naziv}
        turnirLogo={turnirInfo.logo_url}
        lokacija={turnirInfo.lokacija}
        nazivLokacije={turnirInfo.naziv_lokacije}
        features={turnirInfo.features}
      />
      <div className="scorers-container">
        <div className="scorers-header">
          <img src={socerPlayerIcon} alt="Strijelci icon" />
          <h3>Najbolji Strijelci</h3>
        </div>

        <div className="scorers-list">
          {scorers.length > 0 ? (
            scorers.map((player, index) => (
              <div key={`${player.name}-${index}`} className="scorer-item">
                <div className="rank-name">
                  <span
                    className={`rank ${
                      index === 0
                        ? "gold"
                        : index === 1
                          ? "silver"
                          : index === 2
                            ? "bronze"
                            : ""
                    }`}
                  >
                    {index + 1}.
                  </span>
                  <div className="player-info">
                    <span className="player-name">{player.name}</span>
                    <span className="team-name">{player.team}</span>
                  </div>
                </div>

                <div className="goals-count">
                  <span className="count">{player.goals}</span>
                  <i className="fi fi-rs-football"></i>
                </div>
              </div>
            ))
          ) : (
            <div
              className="no-scorers-info"
              style={{ textAlign: "center", padding: "30px", color: "#888" }}
            >
              <p>Trenutno nema strijelaca za prikaz.</p>
            </div>
          )}
        </div>
      </div>
      <HomeFooter />
      <BottomNavigation />
    </>
  );
};

export default HomeScorers;
